import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Shield, Phone, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ConsultationForm from '@/components/common/ConsultationForm';
import { staggerContainer, fadeInUp, textReveal } from '../animations';

/**
 * HeroSection — LCP-critical, eager-loaded.
 * Identical markup, animations, and data-testids preserved from the original
 * HomePage.jsx so SEO + CWV behaviour (preloaded hero image, etc.) is unchanged.
 */
const HeroSection = ({ onWhatsApp }) => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section ref={heroRef} className="relative min-h-[70vh] flex items-center overflow-hidden">
      <motion.div className="absolute inset-0 z-0" style={{ y: heroY }}>
        <img
          src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80&fm=webp"
          alt="Global Business"
          className="w-full h-[120%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/70 to-slate-950" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/50 to-transparent" />
      </motion.div>

      <motion.div
        className="container max-w-7xl mx-auto px-6 md:px-12 relative z-10 pt-32 pb-20"
        style={{ opacity: heroOpacity }}
      >
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.div variants={textReveal} className="mb-8" initial="initial" animate="animate">
              <motion.span
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm font-medium tracking-wide backdrop-blur-sm"
                whileHover={{ scale: 1.02, borderColor: 'rgba(197, 160, 40, 0.3)' }}
                transition={{ duration: 0.2 }}
              >
                <Shield className="w-4 h-4 text-[#C5A028]" />
                <span className="text-slate-300">Government of India Approved Agency</span>
              </motion.span>
            </motion.div>

            <motion.h1
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="font-syne font-bold leading-[0.95] tracking-tight mb-6"
            >
              <motion.span variants={textReveal} className="block text-white text-5xl md:text-6xl lg:text-7xl">
                Best Overseas Recruitment
              </motion.span>
              <motion.span variants={textReveal} className="block text-gradient-gold text-5xl md:text-6xl lg:text-7xl">
                Agency in Delhi
              </motion.span>
              <motion.span
                variants={textReveal}
                className="block text-lg md:text-xl lg:text-2xl font-medium font-manrope text-slate-300 mt-4 tracking-normal"
              >
                <span className="text-[#C5A028]">MEA Approved</span>
                <span className="mx-2 text-slate-500" aria-hidden="true">|</span>
                <span className="text-white">Gulf &amp; Saudi Jobs</span>
              </motion.span>
            </motion.h1>

            <motion.h2 variants={fadeInUp} className="text-2xl md:text-3xl text-slate-300 font-light font-manrope leading-tight mb-4">
              Global Talent. <span className="text-[#C5A028] font-medium">Precision Placed.</span>
            </motion.h2>

            <motion.p variants={fadeInUp} className="text-base lg:text-lg text-slate-400 max-w-xl mb-8 leading-relaxed font-manrope">
              India's MEA-approved recruitment partner specialising in
              <span className="text-white font-medium"> Gulf &amp; Saudi Arabia jobs</span> —
              Royal Saudi Embassy empanelled, with 10,000+ verified placements across
              construction, oil &amp; gas, healthcare, hospitality and engineering.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 mb-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <Button
                  onClick={onWhatsApp}
                  size="lg"
                  className="bg-[#C5A028] text-black hover:bg-[#EAB308] rounded-full px-8 py-6 text-base font-bold transition-all duration-300 btn-gold-glow"
                  data-testid="hero-cta-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Get Free Assessment
                </Button>
              </motion.div>
              <Link to="/about">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="outline"
                    size="lg"
                    className="border border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium backdrop-blur-sm"
                  >
                    Learn More
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden lg:block"
          >
            <div className="glass-premium rounded-3xl p-8 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#C5A028]/20 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-[#C5A028]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white font-syne">Free Assessment</h3>
                  <p className="text-sm text-slate-400">Get expert guidance today</p>
                </div>
              </div>
              <ConsultationForm variant="dark" />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
